import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// ─── inject animations once ──────────────────────────────────────
const STYLE_ID = "pt-anim";
if (!document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes pt-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.85)} }
    @keyframes pt-spin   { to{transform:rotate(360deg)} }
    @keyframes pt-fade   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    @keyframes pt-skel   { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
    .pt-anim { animation: pt-fade .2s ease both }
    .pt-pulse { animation: pt-pulse 1.6s ease-in-out infinite }
    .pt-spin  { animation: pt-spin .8s linear infinite; display:inline-block }
    .pt-skel  {
      background: linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%);
      background-size: 800px 100%;
      animation: pt-skel 1.4s ease-in-out infinite;
      border-radius: 4px;
    }
  `;
  document.head.appendChild(s);
}

// ─── session-persisted credentials ───────────────────────────────
const SS = {
  get: (k, d = "") => { try { return sessionStorage.getItem("pt_" + k) || d; } catch { return d; } },
  set: (k, v)      => { try { sessionStorage.setItem("pt_" + k, v); } catch {} },
};

const TABS = [
  { key: "home",         label: "Home",           icon: "fa-home" },
  { key: "tms",          label: "TMS Push",        icon: "fa-truck" },
  { key: "cw",           label: "CloudWatch",      icon: "fa-cloud" },
  { key: "order",        label: "Order API",       icon: "fa-shopping-cart" },
  { key: "emirates",     label: "Emirates Push",   icon: "fa-plane" },
  { key: "scan-builder", label: "Scan Builder",    icon: "fa-list-ul" },
];

const TOOL_CARDS = [
  { key: "tms",          icon: "fa-truck",        color: "#2563eb", bg: "#eff6ff",
    title: "TMS Push",
    desc: "Look up orders by AWB or booking ID, preview the payload, and push tasks to TMS in one click." },
  { key: "cw",           icon: "fa-cloud",        color: "#7c3aed", bg: "#f5f3ff",
    title: "CloudWatch",
    desc: "Search CloudWatch log groups for booking ID payloads. Uses your AWS session credentials per request." },
  { key: "order",        icon: "fa-shopping-cart",color: "#0891b2", bg: "#ecfeff",
    title: "Order API",
    desc: "Complete or create Shypv orders. Optionally check whether a booking already exists in the DB first." },
  { key: "emirates",     icon: "fa-plane",        color: "#059669", bg: "#ecfdf5",
    title: "Emirates Push",
    desc: "Check AWB status, push delivery events to Emirates spEKtrum with POD, and run the auto-loop." },
  { key: "scan-builder", icon: "fa-list-ul",      color: "#d97706", bg: "#fffbeb",
    title: "Scan Builder",
    desc: "Walk the full go_tasks_history timeline, push scans sequentially, and retry failed entries from the audit log." },
];

export default function PowerTools() {
  const API_URL = process.env.REACT_APP_DRIVER_API_URL || process.env.REACT_APP_API_URL || "http://localhost:5566/";
  const BASE = API_URL.endsWith("/") ? API_URL + "shypvdriverapp" : API_URL + "/shypvdriverapp";

  const [activeTab, setActiveTab] = useState("home");
  // Shared credential state — persisted to sessionStorage so switching tabs doesn't clear them
  const [emiratesCid,  setEmiratesCid]  = useState(() => SS.get("em_cid"));
  const [emiratesCsec, setEmiratesCsec] = useState(() => SS.get("em_csec"));
  const [awsKey,    setAwsKey]    = useState(() => SS.get("aws_key"));
  const [awsSecret, setAwsSecret] = useState(() => SS.get("aws_sec"));
  const [awsToken,  setAwsToken]  = useState(() => SS.get("aws_tok"));

  // Persist credentials whenever they change
  useEffect(() => { SS.set("em_cid",  emiratesCid);  }, [emiratesCid]);
  useEffect(() => { SS.set("em_csec", emiratesCsec); }, [emiratesCsec]);
  useEffect(() => { SS.set("aws_key", awsKey);    }, [awsKey]);
  useEffect(() => { SS.set("aws_sec", awsSecret); }, [awsSecret]);
  useEffect(() => { SS.set("aws_tok", awsToken);  }, [awsToken]);

  // Live loop status for the home-tab badges
  const [loopStatuses, setLoopStatuses] = useState({ emirates: null, scanBuilder: null });
  const pollLoop = useCallback(async () => {
    try {
      const [em, sb] = await Promise.all([
        axios.get(`${BASE}/api/power-tools/emirates/auto/status`).catch(() => null),
        axios.get(`${BASE}/api/power-tools/scan-builder/auto/status`).catch(() => null),
      ]);
      setLoopStatuses({
        emirates: em?.data?.state || null,
        scanBuilder: sb?.data?.state || null,
      });
    } catch {}
  }, [BASE]);
  useEffect(() => { pollLoop(); const t = setInterval(pollLoop, 4000); return () => clearInterval(t); }, [pollLoop]);

  const creds = { emiratesCid, emiratesCsec, setEmiratesCid, setEmiratesCsec, awsKey, awsSecret, awsToken, setAwsKey, setAwsSecret, setAwsToken };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "#0f172a", padding: "0 24px", display: "flex", alignItems: "center", gap: 12, height: 52 }}>
        <i className="fa fa-bolt" style={{ color: "#f59e0b", fontSize: 18 }} />
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 16, letterSpacing: "-0.3px" }}>Power Tools</span>
        <span style={{ color: "#475569", fontSize: 12, marginLeft: 4 }}>Shypv Ops</span>
        <div style={{ flex: 1 }} />
        <LoopStatusPill label="Emirates" state={loopStatuses.emirates} />
        <LoopStatusPill label="Scan Builder" state={loopStatuses.scanBuilder} />
      </div>

      {/* Tab nav */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 24px", display: "flex", gap: 0 }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              padding: "12px 16px", border: "none", background: "transparent",
              borderBottom: activeTab === t.key ? "2px solid #2563eb" : "2px solid transparent",
              fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? "#2563eb" : "#64748b",
              cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6,
              transition: "color .15s",
            }}>
            <i className={`fa ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="pt-anim" key={activeTab} style={{ padding: 24 }}>
        {activeTab === "home"         && <HomePanel onNav={setActiveTab} loopStatuses={loopStatuses} base={BASE} />}
        {activeTab === "tms"          && <TmsPushPanel base={BASE} />}
        {activeTab === "cw"           && <CloudWatchPanel base={BASE} creds={creds} />}
        {activeTab === "order"        && <CreateOrderPanel base={BASE} />}
        {activeTab === "emirates"     && <EmiratesPushPanel base={BASE} creds={creds} />}
        {activeTab === "scan-builder" && <ScanBuilderPanel base={BASE} creds={creds} />}
      </div>
    </div>
  );
}

// ─── Home Panel ───────────────────────────────────────────────────
function HomePanel({ onNav, loopStatuses, base }) {
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.5px" }}>
          Operations Hub
        </h1>
        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 14 }}>
          Internal tooling for TMS, Emirates cargo, CloudWatch and order management. Pick a tool to get started.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginBottom: 28 }}>
        {TOOL_CARDS.map((c) => {
          const running = c.key === "emirates"
            ? loopStatuses.emirates?.running
            : c.key === "scan-builder"
              ? loopStatuses.scanBuilder?.running
              : false;
          return (
            <button key={c.key} onClick={() => onNav(c.key)} style={{
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
              padding: 20, textAlign: "left", cursor: "pointer",
              transition: "box-shadow .15s, border-color .15s",
              display: "flex", flexDirection: "column", gap: 10,
            }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.08)"; e.currentTarget.style.borderColor = c.color; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e2e8f0"; }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fa ${c.icon}`} style={{ color: c.color, fontSize: 18 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 15 }}>{c.title}</div>
                </div>
                {running && (
                  <span className="pt-pulse" style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />
                )}
              </div>
              <p style={{ margin: 0, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{c.desc}</p>
              {running && (
                <div style={{ padding: "4px 10px", borderRadius: 20, background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 600, alignSelf: "flex-start" }}>
                  ● Loop running
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────
function Card({ title, subtitle, children, action }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
      {(title || action) && (
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}

function Stat({ label, value, color = "#0f172a", icon }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
        {icon && <i className={`fa ${icon}`} style={{ fontSize: 14 }} />}
        {value ?? 0}
      </div>
    </div>
  );
}

function LoopStatusPill({ label, state }) {
  if (!state) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20,
                  background: state.running ? "#14532d" : "#1e293b", fontSize: 11, color: state.running ? "#86efac" : "#64748b" }}>
      <span className={state.running ? "pt-pulse" : ""}
            style={{ width: 6, height: 6, borderRadius: "50%", background: state.running ? "#4ade80" : "#475569", display: "inline-block" }} />
      {label}: {state.running ? `${state.pushed} pushed` : "idle"}
    </div>
  );
}

function Badge({ children, variant = "default" }) {
  const map = {
    default:  { bg: "#f1f5f9", color: "#475569" },
    success:  { bg: "#dcfce7", color: "#166534" },
    danger:   { bg: "#fee2e2", color: "#991b1b" },
    warn:     { bg: "#fef3c7", color: "#92400e" },
    info:     { bg: "#dbeafe", color: "#1e40af" },
    running:  { bg: "#dcfce7", color: "#166534" },
  };
  const s = map[variant] || map.default;
  return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>{children}</span>;
}

function Alert({ type = "error", children }) {
  const map = { error: { bg: "#fee2e2", color: "#991b1b", icon: "fa-exclamation-triangle" },
                warn:  { bg: "#fef3c7", color: "#92400e", icon: "fa-exclamation-circle" },
                info:  { bg: "#dbeafe", color: "#1e40af", icon: "fa-info-circle" } };
  const s = map[type] || map.error;
  return (
    <div style={{ padding: "10px 14px", borderRadius: 8, background: s.bg, color: s.color, fontSize: 13, marginTop: 10, display: "flex", alignItems: "flex-start", gap: 8 }}>
      <i className={`fa ${s.icon}`} style={{ marginTop: 2 }} />
      <span>{children}</span>
    </div>
  );
}

function Skel({ h = 20, w = "100%" }) {
  return <div className="pt-skel" style={{ height: h, width: w, display: "block" }} />;
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, style: extraStyle }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder}
             style={{ ...INPUT, ...extraStyle, paddingRight: 34 }} />
      <i className={`fa ${show ? "fa-eye-slash" : "fa-eye"}`}
         onClick={() => setShow((s) => !s)}
         style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#94a3b8" }} />
    </div>
  );
}

function Spinner() { return <span className="pt-spin"><i className="fa fa-spinner" /></span>; }

function DataTable({ cols, rows, emptyMsg = "No data" }) {
  if (!rows || rows.length === 0)
    return <div style={{ padding: "24px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>{emptyMsg}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>{cols.map((c) => <th key={c.key || c} style={TH}>{typeof c === "object" ? (c.label ?? "") : c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
              {cols.map((c) => <td key={c.key || c} style={TD}>{c.render ? c.render(row) : (row[c.key || c] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────
const INPUT = { width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, color: "#0f172a", boxSizing: "border-box", outline: "none", background: "#fff" };
const TEXTAREA = { ...INPUT, fontFamily: "ui-monospace, monospace", resize: "vertical" };
const TH = { textAlign: "left", padding: "8px 12px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const TD = { padding: "9px 12px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", verticalAlign: "top" };

const BTN = { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none", transition: "opacity .15s" };
const B = {
  primary: { ...BTN, background: "#2563eb", color: "#fff" },
  success: { ...BTN, background: "#16a34a", color: "#fff" },
  danger:  { ...BTN, background: "#dc2626", color: "#fff" },
  ghost:   { ...BTN, background: "#fff",    color: "#475569", border: "1px solid #e2e8f0" },
  text:    { ...BTN, background: "transparent", color: "#2563eb", padding: "6px 8px" },
};

const fmtUtc = (s) => {
  if (!s) return "—";
  const str = String(s).replace("T", " ").replace("Z", "").trim();
  return str.length > 19 ? str.slice(0, 19) + " UTC" : str + " UTC";
};

// Events that require an exception code per the Emirates spEKtrum spec.
// For others (POD, OFD, OIS, ARR, …) "no exception" is correct — render
// as "n/a" instead of "—" to make that intent explicit.
const EXC_REQUIRED_EVENTS = new Set(["DLX", "SHX", "CRX", "RDL"]);

function ExcCode({ event, code }) {
  const required = event && EXC_REQUIRED_EVENTS.has(String(event));
  if (code) return <code style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 3, fontWeight: 600 }} title="Locked — server-resolved exception code">{code}</code>;
  if (required) return <code title={`Event ${event} requires an exception code but none was resolved on the server`} style={{ background: "#fee2e2", color: "#991b1b", padding: "1px 6px", borderRadius: 3, fontWeight: 600 }}>MISSING</code>;
  return <span title={`Event ${event || "?"} does not carry an exception code per Emirates spec`} style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>;
}

// Expandable JSON viewer for log rows. Click the chevron to open and see
// the request_payload sent to Emirates plus the raw response.
function JsonRow({ open, label, value }) {
  if (!open) return null;
  return (
    <tr style={{ background: "#fafbff" }}>
      <td colSpan={99} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", letterSpacing: ".06em", marginBottom: 4, textTransform: "uppercase" }}>{label}</div>
        <pre style={{ margin: 0, padding: 12, fontSize: 11, background: "#0f172a", color: "#a5f3fc", overflowX: "auto", borderRadius: 6, maxHeight: 360 }}>
          {value === null || value === undefined || value === ""
            ? <span style={{ color: "#64748b" }}>—</span>
            : typeof value === "string" ? value : JSON.stringify(value, null, 2)}
        </pre>
      </td>
    </tr>
  );
}

// Dedicated auto-loop log table with row expansion (DataTable doesn't support that).
function AutoLogTable({ rows, fields }) {
  const [openIdx, setOpenIdx] = useState(null);
  if (!rows || rows.length === 0)
    return <div style={{ padding: "24px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No entries yet</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 26 }}></th>
            {fields.map((f) => <th key={f.key} style={TH}>{f.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <React.Fragment key={i}>
              <tr style={{ background: openIdx === i ? "#f8fafc" : i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                  onClick={() => setOpenIdx(openIdx === i ? null : i)}>
                <td style={TD}>
                  <i className={`fa fa-chevron-${openIdx === i ? "down" : "right"}`}
                     style={{ color: "#64748b", fontSize: 10 }} />
                </td>
                {fields.map((f) => <td key={f.key} style={TD}>{f.render ? f.render(r) : (r[f.key] ?? "—")}</td>)}
              </tr>
              <JsonRow open={openIdx === i} label="Request payload (sent to Emirates)" value={r.request_payload} />
              <JsonRow open={openIdx === i} label="Response body (from Emirates)"      value={r.response_body || r.response} />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── TMS Push Panel ───────────────────────────────────────────────
// Faithful port of app.py's TMS flow:
//   AWB mode:        Check DB → Copy Selected → Push (console log)
//   Booking-ID mode: paste, optional "Format Booking IDs" toggle, Preview → Push
// Paste handling matches app.py's parseLines exactly:
//   raw.split(/[\n,;\s]+/)  →  trim  →  filter empty  →  Set dedupe
// extractBookingIds(...) lifts `UB…` IDs out of messy pasted text using the
// same regex app.py uses.

const parseLines = (raw) => {
  if (!raw) return [];
  return [...new Set(
    String(raw).split(/[\n,;\s]+/).map((s) => s.trim()).filter((s) => s.length > 0)
  )];
};

const extractBookingIds = (raw) => {
  const matches = String(raw).match(/\bUB[0-9A-Z-]+(?:_[0-9]+)?\b/ig) || [];
  return [...new Set(matches.map((s) => s.trim().toUpperCase()).filter(Boolean))];
};

function TmsPushPanel({ base }) {
  const [mode, setMode] = useState("awb");

  // ── shared state ──
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyAwb, setHistoryAwb] = useState("");
  const [historyOnlyFailed, setHistoryOnlyFailed] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [skipAlreadyPushed, setSkipAlreadyPushed] = useState(true);

  const refreshHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { limit: 100 };
      if (historyAwb) params.awb = historyAwb.trim();
      if (historyOnlyFailed) params.only_failed = "1";
      const r = await axios.get(`${base}/api/power-tools/tms/logs`, { params });
      setHistory(r.data?.logs || []);
    } catch (e) { /* table missing or other err — silent */ }
    finally { setHistoryLoading(false); }
  };
  useEffect(() => { refreshHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const retryLog = async (id) => {
    setRetryingId(id);
    try { await axios.post(`${base}/api/power-tools/tms/retry/${id}`); await refreshHistory(); }
    catch (e) { /* surface via history panel */ }
    finally { setRetryingId(null); }
  };

  return (
    <div>
      {/* Mode toggle — same two flows app.py provides */}
      <Card title="TMS Push" subtitle="Push tasks to TMS by AWB (3-step flow) or directly by booking ID">
        <div style={{ display: "flex", gap: 24, marginBottom: 4 }}>
          {[["awb", "By AWB", "/api/tms/check + /push"], ["booking", "By Booking ID", "/api/tms/preview-booking + /push-booking"]].map(([k, l, sub]) => (
            <label key={k} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
              <input type="radio" checked={mode === k} onChange={() => setMode(k)} />
              <span style={{ fontSize: 13 }}><strong>{l}</strong> <span style={{ color: "#94a3b8", fontSize: 11 }}>{sub}</span></span>
            </label>
          ))}
          <div style={{ flex: 1 }} />
          <label style={{ fontSize: 12, color: "#475569", display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={skipAlreadyPushed} onChange={(e) => setSkipAlreadyPushed(e.target.checked)} />
            Skip tasks already pushed via Power Tools
          </label>
        </div>
      </Card>

      {mode === "awb"
        ? <TmsAwbFlow base={base} skipAlreadyPushed={skipAlreadyPushed} onRefreshHistory={refreshHistory} />
        : <TmsBookingFlow base={base} skipAlreadyPushed={skipAlreadyPushed} onRefreshHistory={refreshHistory} />}

      <Card title={`TMS Push History — ${history.length}`}
            subtitle="Persisted in primary DB · click Retry to re-push (skip check bypassed)"
            action={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={historyAwb} onChange={(e) => setHistoryAwb(e.target.value)} placeholder="filter AWB"
                       style={{ ...INPUT, width: 140, fontSize: 12 }} />
                <label style={{ fontSize: 11, color: "#475569", display: "flex", gap: 4, alignItems: "center" }}>
                  <input type="checkbox" checked={historyOnlyFailed} onChange={(e) => setHistoryOnlyFailed(e.target.checked)} /> Failed only
                </label>
                <button onClick={refreshHistory} disabled={historyLoading} style={B.ghost}>
                  {historyLoading ? <Spinner /> : <i className="fa fa-refresh" />}
                </button>
              </div>
            }>
        <DataTable
          cols={[
            { key: "task_id",         label: "Task" },
            { key: "business_ref_no", label: "AWB" },
            { key: "booking_id",      label: "Booking" },
            { key: "job_type",        label: "Type" },
            { key: "http_status",     label: "HTTP",  render: (r) => <Badge variant={r.success ? "success" : "danger"}>{r.http_status ?? "—"}</Badge> },
            { key: "attempts",        label: "Tries" },
            { key: "updated_at",      label: "Updated (UTC)", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtUtc(r.updated_at)}</span> },
            { key: "last_error",      label: "Error", render: (r) => <span style={{ fontSize: 11, color: "#dc2626", fontFamily: "monospace" }}>{r.last_error || "—"}</span> },
            { key: "_retry",          label: "", render: (r) => (
              <button onClick={() => retryLog(r.id)} disabled={retryingId === r.id} style={{ ...B.primary, padding: "4px 10px", fontSize: 11 }}>
                {retryingId === r.id ? <Spinner /> : <><i className="fa fa-redo" /> retry</>}
              </button>
            )},
          ]}
          rows={history}
          emptyMsg="No TMS pushes recorded yet"
        />
      </Card>
    </div>
  );
}

// ─── AWB Flow (3-step) ────────────────────────────────────────────
// Step 1: paste AWBs → Check DB
// Step 2: select tasks → Copy Selected to Push (confirmation)
// Step 3: Push to TMS → console-style log
function TmsAwbFlow({ base, skipAlreadyPushed, onRefreshHistory }) {
  const [rawText, setRawText] = useState("");
  const [step, setStep] = useState(1);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false); // true once a check returned (even with 0 rows)

  const [tasks, setTasks] = useState([]);
  const [missing, setMissing] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [pushQueue, setPushQueue] = useState([]);   // confirmation list

  const [pushing, setPushing] = useState(false);
  const [logLines, setLogLines] = useState([]);
  const [pushResult, setPushResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setRawText(""); setTasks([]); setMissing([]); setSelected(new Set());
    setPushQueue([]); setLogLines([]); setPushResult(null); setError("");
    setStep(1); setChecked(false);
  };

  const checkDb = async () => {
    setError(""); setChecked(false);
    const awbs = parseLines(rawText);
    if (!awbs.length) { setError("Paste at least one AWB"); return; }
    setChecking(true);
    try {
      const r = await axios.post(`${base}/api/power-tools/tms/check`, { awbs });
      const t = r.data?.tasks || [];
      setTasks(t);
      setMissing(r.data?.missing || []);
      setChecked(true);
      // Default-select pushable tasks (locus_task_sts != SUCCESS && not skipped-as-pt-pushed).
      const sel = new Set();
      t.forEach((task) => {
        const alreadyByLocus = task.locus_task_sts === "SUCCESS";
        const alreadyByPt = skipAlreadyPushed && task.pt_pushed;
        if (!alreadyByLocus && !alreadyByPt) sel.add(task.task_id);
      });
      setSelected(sel);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setChecking(false); }
  };

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(tasks.map((t) => t.task_id)));
    else setSelected(new Set());
  };
  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const copyToPush = () => {
    const queue = tasks.filter((t) => selected.has(t.task_id));
    if (!queue.length) { setError("Select at least one task"); return; }
    setPushQueue(queue);
    setStep(2);
  };

  const push = async () => {
    if (!pushQueue.length) return;
    setError(""); setLogLines([]); setPushing(true); setStep(3);
    const ids = pushQueue.map((t) => t.task_id);
    appendLog("info", `Pushing ${ids.length} tasks…`);
    try {
      const r = await axios.post(`${base}/api/power-tools/tms/push`, {
        task_ids: ids,
        skip_already_pushed: skipAlreadyPushed,
      });
      const d = r.data || {};
      (d.results || []).forEach((x) => {
        const ok = x.http_status === 200 || x.http_status === 201;
        appendLog(ok ? "ok" : "err",
          `${ok ? "✓" : "✗"} ${x.booking_id} (${x.type}) → HTTP ${x.http_status ?? "ERR"}${ok ? "" : ": " + (x.response || "").slice(0, 120)}`);
      });
      appendLog("info", "");
      appendLog("info", `Done: ${d.success} ok, ${d.failed} fail`);
      if (d.skipped_already_pushed) appendLog("warn", `Skipped: ${d.skipped_already_pushed} task(s) already pushed via Power Tools`);
      if (d.db_updated) appendLog("ok", `DB updated: ${d.db_updated} tasks → SUCCESS (locus_task_sts)`);
      setPushResult(d);
      onRefreshHistory && onRefreshHistory();
    } catch (e) {
      appendLog("err", e?.response?.data?.error || e.message);
    } finally { setPushing(false); }
  };

  const appendLog = (kind, text) => setLogLines((cur) => [...cur, { kind, text }]);
  const allChecked = tasks.length > 0 && tasks.every((t) => selected.has(t.task_id));

  return (
    <>
      {/* Stepper */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["1 · Check DB", "2 · Copy to Push", "3 · Push to TMS"].map((label, i) => {
          const n = i + 1;
          const active = step === n, done = step > n;
          return (
            <div key={n} style={{
              flex: 1, padding: "8px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              textAlign: "center",
              background: active ? "#dbeafe" : done ? "#dcfce7" : "#f1f5f9",
              color:      active ? "#1e40af" : done ? "#166534" : "#94a3b8",
            }}>{done ? "✓ " : ""}{label}</div>
          );
        })}
      </div>

      {step === 1 && (
        <Card title="Step 1 — Check DB" subtitle="Paste AWBs (newline, comma, semicolon or space separated)">
          <textarea rows={5} value={rawText} onChange={(e) => setRawText(e.target.value)}
                    placeholder={"310100554473\n310100554528"}
                    style={TEXTAREA} />
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button onClick={checkDb} disabled={checking} style={B.primary}>
              {checking ? <><Spinner /> Checking…</> : <><i className="fa fa-database" /> Check DB</>}
            </button>
            <button onClick={reset} style={B.ghost}><i className="fa fa-refresh" /> Reset</button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center" }}>
              Parsed: {parseLines(rawText).length} unique AWB(s)
            </span>
          </div>
          {error && <Alert>{error}</Alert>}

          {/* Show the result block as soon as a check has run, even if found == 0.
              Previously this section was hidden when tasks.length === 0, which
              meant a request with only missing AWBs displayed silently — looking
              exactly like the bug "no error is shown" on a missing AWB. */}
          {checked && !checking && (
            <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 14 }}>
              <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Stat label="Found"   value={tasks.length}   color={tasks.length ? "#16a34a" : "#94a3b8"} />
                <Stat label="Missing" value={missing.length} color={missing.length ? "#dc2626" : "#94a3b8"} />
                <Stat label="Pasted"  value={parseLines(rawText).length} color="#475569" />
                <div style={{ flex: 1 }} />
                {tasks.length > 0 && (
                  <button onClick={copyToPush} disabled={!selected.size} style={B.success}>
                    <i className="fa fa-arrow-right" /> Copy Selected to Push ({selected.size})
                  </button>
                )}
              </div>
              {tasks.length === 0 && missing.length > 0 && (
                <Alert type="warn">
                  <strong>No tasks found in DB for any of the {missing.length} AWB(s) you pasted.</strong>
                  <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>
                    {missing.join(", ")}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: "#92400e" }}>
                    Power Tools queries <code>go_tasks.business_ref_no</code> on <code>prod_api_shypv</code>.
                    Verify the AWB exists in that DB (not the driver-app DB). Common causes: typo,
                    wrong DB target, or AWB belongs to a partner that uses a different
                    {' '}<code>business_ref_no</code> format.
                  </div>
                </Alert>
              )}
              {tasks.length > 0 && missing.length > 0 && (
                <Alert type="warn">Missing in DB: <code style={{ fontSize: 11 }}>{missing.join(", ")}</code></Alert>
              )}
              {tasks.length > 0 && (
                <DataTable
                  cols={[
                    { key: "_chk", label: <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />,
                      render: (t) => <input type="checkbox" checked={selected.has(t.task_id)} onChange={() => toggle(t.task_id)} /> },
                    { key: "business_ref_no", label: "AWB" },
                    { key: "booking_id",      label: "Booking" },
                    { key: "job_type",        label: "Type" },
                    { key: "task_status",     label: "Status" },
                    { key: "locus_task_sts",  label: "TMS",
                      render: (t) => <Badge variant={t.locus_task_sts === "SUCCESS" ? "success" : t.locus_task_sts === "FAILED" ? "danger" : "warn"}>{t.locus_task_sts || "NULL"}</Badge> },
                    { key: "pt_pushed",       label: "PT",
                      render: (t) => t.pt_pushed ? <Badge variant="success">pushed</Badge> : <span style={{ color: "#cbd5e1" }}>—</span> },
                    { key: "team_id",         label: "Team" },
                    { key: "name",            label: "Name" },
                  ]}
                  rows={tasks}
                />
              )}
            </div>
          )}
        </Card>
      )}

      {step === 2 && (
        <Card title={`Step 2 — Confirm Push (${pushQueue.length} tasks across ${new Set(pushQueue.map((t) => t.business_ref_no)).size} orders)`}>
          <DataTable
            cols={[
              { key: "business_ref_no", label: "AWB" },
              { key: "booking_id",      label: "Booking" },
              { key: "job_type",        label: "Type" },
              { key: "name",            label: "Name" },
              { key: "phone",           label: "Phone" },
              { key: "job_address",     label: "Address", render: (t) => <span style={{ fontSize: 11 }}>{(t.job_address || "").slice(0, 50)}</span> },
            ]}
            rows={pushQueue}
          />
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button onClick={() => setStep(1)} style={B.ghost}><i className="fa fa-arrow-left" /> Back</button>
            <div style={{ flex: 1 }} />
            <button onClick={push} disabled={pushing} style={B.success}>
              {pushing ? <><Spinner /> Pushing…</> : <><i className="fa fa-paper-plane" /> Push to TMS</>}
            </button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card title="Step 3 — Push Log"
              action={<button onClick={reset} style={B.primary}><i className="fa fa-refresh" /> Start Over</button>}>
          <div style={{ background: "#0f172a", borderRadius: 8, padding: 14, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#e2e8f0", maxHeight: 360, overflowY: "auto" }}>
            {logLines.length === 0
              ? <span style={{ color: "#64748b" }}>Pushing…</span>
              : logLines.map((l, i) => {
                const color = l.kind === "ok" ? "#4ade80" : l.kind === "err" ? "#f87171" : l.kind === "warn" ? "#fbbf24" : "#a5f3fc";
                return <div key={i} style={{ color, whiteSpace: "pre" }}>{l.text || " "}</div>;
              })}
          </div>
          {pushResult && (
            <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
              <Stat label="Success" value={pushResult.success} color="#16a34a" />
              <Stat label="Failed"  value={pushResult.failed}  color="#dc2626" />
              <Stat label="Skipped (PT)" value={pushResult.skipped_already_pushed} color="#94a3b8" />
              <Stat label="DB Updated"   value={pushResult.db_updated} color="#2563eb" />
            </div>
          )}
        </Card>
      )}
    </>
  );
}

// ─── Booking-ID Flow (preview + push) ─────────────────────────────
// Mirrors panel-direct in app.py. Optional "Format Booking IDs" toggle uses
// extractBookingIds to pull `UB…` IDs out of messy pasted text.
function TmsBookingFlow({ base, skipAlreadyPushed, onRefreshHistory }) {
  const [rawText, setRawText] = useState("");
  const [formatToggle, setFormatToggle] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [missing, setMissing] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [updateDb, setUpdateDb] = useState(true);
  const [logLines, setLogLines] = useState([]);
  const [pushResult, setPushResult] = useState(null);
  const [error, setError] = useState("");

  const resolvedIds = formatToggle ? extractBookingIds(rawText) : parseLines(rawText);

  const formatNow = () => {
    if (!formatToggle) return;
    const ids = extractBookingIds(rawText);
    if (ids.length) setRawText(ids.join("\n"));
  };

  const preview = async () => {
    setError(""); setPreviewRows([]); setMissing([]); setPushResult(null); setLogLines([]); setPreviewed(false);
    formatNow();
    const bids = resolvedIds;
    if (!bids.length) { setError("Paste at least one booking ID"); return; }
    setPreviewing(true);
    try {
      const r = await axios.post(`${base}/api/power-tools/tms/preview-booking`, { booking_ids: bids });
      setPreviewRows(r.data?.tasks || []);
      setMissing(r.data?.missing || []);
      setPreviewed(true);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setPreviewing(false); }
  };

  const push = async () => {
    formatNow();
    const bids = resolvedIds;
    if (!bids.length) { setError("Paste at least one booking ID"); return; }
    setPushing(true); setError(""); setPushResult(null); setLogLines([]);
    appendLog("info", "Fetching tasks from DB…");
    try {
      const r = await axios.post(`${base}/api/power-tools/tms/push-booking`, {
        booking_ids: bids,
        update_db: updateDb,
        skip_already_pushed: skipAlreadyPushed,
      });
      const d = r.data || {};
      if (d.error) { appendLog("err", d.error); return; }
      appendLog("info", `Found ${d.found} tasks, pushing…`);
      appendLog("info", "");
      (d.results || []).forEach((x) => {
        const ok = x.http_status === 200 || x.http_status === 201;
        appendLog(ok ? "ok" : "err",
          `${ok ? "✓" : "✗"} ${x.booking_id} (${x.type}) → HTTP ${x.http_status ?? "ERR"}${ok ? "" : ": " + (x.response || "").slice(0, 100)}`);
      });
      appendLog("info", "");
      appendLog("info", `Done: ${d.success} success, ${d.failed} failed`);
      if (d.skipped_already_pushed) appendLog("warn", `Skipped: ${d.skipped_already_pushed} task(s) already pushed via Power Tools`);
      if (d.db_updated) appendLog("ok", `DB updated: ${d.db_updated} tasks → SUCCESS (locus_task_sts)`);
      if (d.missing && d.missing.length) appendLog("warn", `Missing: ${d.missing.join(", ")}`);
      setPushResult(d);
      onRefreshHistory && onRefreshHistory();
    } catch (e) {
      appendLog("err", e?.response?.data?.error || e.message);
    } finally { setPushing(false); }
  };
  const appendLog = (kind, text) => setLogLines((cur) => [...cur, { kind, text }]);

  return (
    <Card title="Direct Push by Booking ID"
          subtitle="Paste booking IDs (one per line, comma/semicolon/space all work). Preview rebuilds the TMS payload using build_tms_payload_exact.">
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 8 }}>
        <label style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={formatToggle} onChange={(e) => setFormatToggle(e.target.checked)} />
          Format booking IDs (extract <code>UB…</code> from messy paste)
        </label>
        <label style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={updateDb} onChange={(e) => setUpdateDb(e.target.checked)} />
          Update locus_task_sts → SUCCESS on 2xx
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "#94a3b8" }}>Parsed: {resolvedIds.length} unique ID(s)</span>
      </div>
      <textarea rows={5} value={rawText} onChange={(e) => setRawText(e.target.value)}
                placeholder={"UB30-26D5700\nUB30-26D5699\n(paste anything — toggle above to auto-extract)"}
                style={TEXTAREA} />
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        <button onClick={preview} disabled={previewing} style={B.ghost}>
          {previewing ? <><Spinner /> Loading…</> : <><i className="fa fa-eye" /> Preview Payloads</>}
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={push} disabled={pushing} style={B.success}>
          {pushing ? <><Spinner /> Pushing…</> : <><i className="fa fa-paper-plane" /> Fetch & Push to TMS</>}
        </button>
      </div>
      {error && <Alert>{error}</Alert>}

      {/* Show the preview block as soon as a preview returned, even if found == 0.
          Previously hidden when previewRows.length === 0 — making the failure
          look like "nothing happened, no error" on a booking ID that doesn't exist. */}
      {previewed && !previewing && (
        <div style={{ marginTop: 14, borderTop: "1px solid #e2e8f0", paddingTop: 14 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            <Stat label="Found"   value={previewRows.length} color={previewRows.length ? "#16a34a" : "#94a3b8"} />
            <Stat label="Missing" value={missing.length}    color={missing.length ? "#dc2626" : "#94a3b8"} />
            <Stat label="Pasted"  value={resolvedIds.length} color="#475569" />
          </div>
          {previewRows.length === 0 && missing.length > 0 && (
            <Alert type="warn">
              <strong>No tasks found in DB for any of the {missing.length} booking ID(s).</strong>
              <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>
                {missing.join(", ")}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: "#92400e" }}>
                Power Tools queries <code>go_tasks.booking_id</code> on <code>prod_api_shypv</code>.
                The lookup also probes <code>_1</code>/<code>_2</code> suffix variants for pickup/drop legs.
                If app.py finds this same ID, both apps target the same DB — double-check the toggle above
                isn't stripping the wrong characters and confirm you're not in AWB mode by mistake.
              </div>
            </Alert>
          )}
          {previewRows.length > 0 && missing.length > 0 && (
            <Alert type="warn">Missing: <code style={{ fontSize: 11 }}>{missing.join(", ")}</code></Alert>
          )}
          {previewRows.map((row) => (
            <details key={row.task_id} style={{ marginBottom: 6, border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <summary style={{ padding: "10px 14px", cursor: "pointer", background: "#f8fafc", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 13 }}>{row.booking_id}</strong>
                <Badge variant="info">{row.job_type}</Badge>
                <Badge variant={row.locus_task_sts === "SUCCESS" ? "success" : row.locus_task_sts === "FAILED" ? "danger" : "warn"}>
                  {row.locus_task_sts || "NULL"}
                </Badge>
                {row.pt_pushed && <Badge variant="success">PT pushed</Badge>}
              </summary>
              <pre style={{ margin: 0, padding: 14, fontSize: 11, background: "#0f172a", color: "#a5f3fc", overflowX: "auto", maxHeight: 240 }}>
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      )}

      {logLines.length > 0 && (
        <div style={{ marginTop: 14, background: "#0f172a", borderRadius: 8, padding: 14, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#e2e8f0", maxHeight: 360, overflowY: "auto" }}>
          {logLines.map((l, i) => {
            const color = l.kind === "ok" ? "#4ade80" : l.kind === "err" ? "#f87171" : l.kind === "warn" ? "#fbbf24" : "#a5f3fc";
            return <div key={i} style={{ color, whiteSpace: "pre" }}>{l.text || " "}</div>;
          })}
        </div>
      )}
      {pushResult && (
        <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
          <Stat label="Success" value={pushResult.success} color="#16a34a" />
          <Stat label="Failed"  value={pushResult.failed}  color="#dc2626" />
          <Stat label="Skipped (PT)" value={pushResult.skipped_already_pushed} color="#94a3b8" />
          <Stat label="DB Updated"   value={pushResult.db_updated} color="#2563eb" />
        </div>
      )}
    </Card>
  );
}

// ─── CloudWatch Panel ─────────────────────────────────────────────
function CloudWatchPanel({ base, creds }) {
  const { awsKey, awsSecret, awsToken, setAwsKey, setAwsSecret, setAwsToken } = creds;
  const [region, setRegion] = useState(SS.get("aws_region", "me-central-1"));
  const [logGroup, setLogGroup] = useState(SS.get("aws_lg", "/ec2/prod-api.shypv.com"));
  const [daysBack, setDaysBack] = useState(30);
  const [groups, setGroups] = useState([]);
  const [listing, setListing] = useState(false);
  const [bookingsText, setBookingsText] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { SS.set("aws_region", region); }, [region]);
  useEffect(() => { SS.set("aws_lg", logGroup); }, [logGroup]);

  const listGroups = async () => {
    if (!awsKey || !awsSecret || !awsToken) return setError("AWS credentials required");
    setListing(true); setError("");
    try {
      const r = await axios.post(`${base}/api/power-tools/cw/list-groups`, { aws_key: awsKey, aws_secret: awsSecret, aws_token: awsToken, region });
      if (r.data?.error) { setError(r.data.error); setGroups([]); }
      else setGroups(r.data?.groups || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setListing(false); }
  };

  const search = async () => {
    const bids = bookingsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!bids.length) return setError("Paste at least one booking ID");
    setSearching(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${base}/api/power-tools/cw/search`, { booking_ids: bids, aws_key: awsKey, aws_secret: awsSecret, aws_token: awsToken, days_back: Number(daysBack) || 30, region, log_group: logGroup });
      setResult(r.data);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setSearching(false); }
  };

  return (
    <div>
      <Card title="AWS Session Credentials" subtitle="Sent per-request, never stored server-side">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Access Key ID"><input value={awsKey} onChange={(e) => setAwsKey(e.target.value)} placeholder="ASIA…" style={INPUT} /></Field>
          <Field label="Secret Access Key"><PasswordInput value={awsSecret} onChange={(e) => setAwsSecret(e.target.value)} /></Field>
          <Field label="Session Token" hint="Required for SSO / assumed-role sessions">
            <textarea rows={3} value={awsToken} onChange={(e) => setAwsToken(e.target.value)} placeholder="IQoJb3Jp…" style={TEXTAREA} />
          </Field>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Region"><input value={region} onChange={(e) => setRegion(e.target.value)} style={INPUT} /></Field>
            <Field label="Log Group">
              <div style={{ display: "flex", gap: 6 }}>
                <input value={logGroup} onChange={(e) => setLogGroup(e.target.value)} style={{ ...INPUT, flex: 1 }} />
                <button onClick={listGroups} disabled={listing} style={B.ghost}>
                  {listing ? <Spinner /> : <i className="fa fa-list" />}
                </button>
              </div>
              {groups.length > 0 && (
                <select value={logGroup} onChange={(e) => setLogGroup(e.target.value)} style={{ ...INPUT, marginTop: 6, fontSize: 12 }}>
                  {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
            </Field>
          </div>
        </div>
        {error && <Alert>{error}</Alert>}
      </Card>

      <Card title="Search Logs">
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <Field label="Booking IDs" style={{ flex: 1 }}>
            <textarea rows={4} value={bookingsText} onChange={(e) => setBookingsText(e.target.value)} placeholder={"BKG12345\nBKG12346"} style={TEXTAREA} />
          </Field>
          <Field label="Days back" style={{ width: 100 }}>
            <input type="number" min={1} max={365} value={daysBack} onChange={(e) => setDaysBack(e.target.value)} style={INPUT} />
          </Field>
        </div>
        <div style={{ marginTop: 10 }}>
          <button onClick={search} disabled={searching} style={B.primary}>
            {searching ? <><Spinner /> Searching…</> : <><i className="fa fa-search" /> Search</>}
          </button>
        </div>
      </Card>

      {searching && <Card title="Searching CloudWatch…"><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{[100, 80, 90].map((w, i) => <Skel key={i} h={16} w={`${w}%`} />)}</div></Card>}

      {result && (
        <Card title={`${result.total_found ?? 0} of ${result.total_searched ?? 0} booking IDs found`}
              subtitle={`Search range: ${result.search_range}`}>
          {(result.results || []).map((row) => (
            <details key={row.booking_id} open={row.found} style={{ marginBottom: 8, border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <summary style={{ padding: "10px 14px", cursor: "pointer", background: row.found ? "#f0fdf4" : "#fef2f2", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
                <Badge variant={row.found ? "success" : "danger"}>{row.found ? `${row.count} entries` : "not found"}</Badge>
                <code style={{ fontWeight: 600 }}>{row.booking_id}</code>
                {row.error && <span style={{ fontSize: 12, color: "#dc2626" }}>{row.error}</span>}
              </summary>
              {(row.entries || []).map((e, i) => (
                <div key={i} style={{ borderTop: "1px solid #f1f5f9", padding: 12 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                    <Badge variant={e.log_type === "request" ? "info" : e.log_type === "response" ? "success" : "default"}>{e.log_type}</Badge>
                    <code style={{ fontSize: 11, color: "#64748b" }}>{e.timestamp}</code>
                  </div>
                  <pre style={{ margin: 0, padding: 10, fontSize: 11, background: "#0f172a", color: "#a5f3fc", overflowX: "auto", borderRadius: 6 }}>
                    {e.payload ? JSON.stringify(e.payload, null, 2) : e.raw_message}
                  </pre>
                </div>
              ))}
            </details>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── Create Order Panel ───────────────────────────────────────────
function CreateOrderPanel({ base }) {
  const [method, setMethod] = useState("complete");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [authing, setAuthing] = useState(false);
  const [checkIds, setCheckIds] = useState("");
  const [checking, setChecking] = useState(false);
  const [dbResult, setDbResult] = useState(null);
  const [payloadText, setPayloadText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [error, setError] = useState("");

  const getToken = async () => {
    if (!email || !password) return setError("Email and password required");
    setAuthing(true); setError("");
    try {
      const r = await axios.post(`${base}/api/power-tools/order/auth`, { email, password });
      if (r.data?.success) setToken(r.data.token);
      else setError(r.data?.error || "Auth failed");
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setAuthing(false); }
  };

  const checkDb = async () => {
    const bids = checkIds.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!bids.length) return setError("Paste at least one booking ID");
    setChecking(true); setError("");
    try { const r = await axios.post(`${base}/api/power-tools/order/check-db`, { booking_ids: bids }); setDbResult(r.data); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setChecking(false); }
  };

  const sendOrder = async () => {
    let payload; try { payload = JSON.parse(payloadText); } catch { return setError("Invalid JSON"); }
    if (method === "create" && !token) return setError("JWT token required");
    setSending(true); setSendResult(null); setError("");
    try {
      const body = { payload, method };
      if (method === "create") body.token = token;
      const r = await axios.post(`${base}/api/power-tools/order/send`, body);
      setSendResult(r.data);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setSending(false); }
  };

  const ok = sendResult && (sendResult.status === 200 || sendResult.status === 201);

  return (
    <div>
      <Card title="Order Method">
        <div style={{ display: "flex", gap: 20 }}>
          {[["complete", "completeOrder", "No Auth Required"], ["create", "createOrder", "JWT Required"]].map(([k, l, sub]) => (
            <label key={k} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
              <input type="radio" checked={method === k} onChange={() => setMethod(k)} />
              <span style={{ fontSize: 13 }}><strong>{l}</strong> <span style={{ color: "#94a3b8", fontSize: 11 }}>{sub}</span></span>
            </label>
          ))}
        </div>
        {method === "create" && (
          <div style={{ marginTop: 14, padding: 14, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 10 }}>
              <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" style={INPUT} /></Field>
              <Field label="Password"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
              <button onClick={getToken} disabled={authing} style={B.primary}>{authing ? <Spinner /> : <><i className="fa fa-key" /> Get Token</>}</button>
            </div>
            <Field label="JWT Token (or paste directly)">
              <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJhbGci…" style={{ ...INPUT, fontFamily: "monospace", fontSize: 11 }} />
            </Field>
          </div>
        )}
        {error && <Alert>{error}</Alert>}
      </Card>

      <Card title="Check DB" subtitle="Optional — verify booking doesn't already exist">
        <Field label="Booking IDs">
          <textarea rows={3} value={checkIds} onChange={(e) => setCheckIds(e.target.value)} placeholder={"UB30-26D5700"} style={TEXTAREA} />
        </Field>
        <div style={{ marginTop: 10 }}>
          <button onClick={checkDb} disabled={checking} style={B.ghost}>{checking ? <Spinner /> : <><i className="fa fa-database" /> Check DB</>}</button>
        </div>
        {dbResult && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            {[["Orders", dbResult.orders, ["order_id", "booking_id", "payment_type", "total_amount"]],
              ["Tasks",  dbResult.tasks,  ["task_id", "booking_id", "job_type", "task_status"]]].map(([t, rows, ks]) => (
              <div key={t} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", background: "#f8fafc", fontSize: 12, fontWeight: 600, color: "#475569" }}>{t} ({(rows || []).length})</div>
                <DataTable cols={ks.map((k) => ({ key: k, label: k }))} rows={rows || []} emptyMsg={`No ${t.toLowerCase()}`} />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Payload" action={
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { try { setPayloadText(JSON.stringify(JSON.parse(payloadText), null, 2)); } catch { setError("Invalid JSON"); } }} style={B.ghost}><i className="fa fa-code" /> Format</button>
          <button onClick={sendOrder} disabled={sending || !payloadText.trim()} style={B.success}>
            {sending ? <><Spinner /> Sending…</> : <><i className="fa fa-paper-plane" /> Send {method === "complete" ? "completeOrder" : "createOrder"}</>}
          </button>
        </div>
      }>
        <textarea rows={12} value={payloadText} onChange={(e) => setPayloadText(e.target.value)} placeholder='{"bkrefid":"…"}' style={TEXTAREA} />
        {error && <Alert>{error}</Alert>}
      </Card>

      {sendResult && (
        <Card title="Response" action={<Badge variant={ok ? "success" : "danger"}>HTTP {sendResult.status ?? "ERR"}</Badge>}>
          <pre style={{ margin: 0, padding: 14, fontSize: 12, background: "#0f172a", color: "#a5f3fc", overflowX: "auto", borderRadius: 8 }}>
            {typeof sendResult.response === "string" ? sendResult.response : JSON.stringify(sendResult.response, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
}

// ─── Emirates Push Panel ──────────────────────────────────────────
function EmiratesPushPanel({ base, creds }) {
  const { emiratesCid, emiratesCsec, setEmiratesCid, setEmiratesCsec } = creds;
  const [authResult, setAuthResult] = useState(null);
  const [authing, setAuthing] = useState(false);
  const [awbsText, setAwbsText] = useState("");
  const [checking, setChecking] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [missing, setMissing] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [podDataUrl, setPodDataUrl] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [error, setError] = useState("");

  const testAuth = async () => {
    setAuthing(true); setError(""); setAuthResult(null);
    try {
      const body = {}; if (emiratesCid) body.client_id = emiratesCid; if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/emirates/auth`, body);
      setAuthResult(r.data);
      if (r.data && !r.data.success) setError(r.data.error || "Auth failed");
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setAuthing(false); }
  };

  const check = async () => {
    const awbs = awbsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!awbs.length) return setError("Paste at least one AWB");
    setChecking(true); setError(""); setTasks([]); setMissing([]); setSelected(new Set()); setPushResult(null);
    try {
      const r = await axios.post(`${base}/api/power-tools/emirates/check`, { awbs });
      setTasks(r.data?.tasks || []);
      setMissing(r.data?.missing || []);
      setSelected(new Set((r.data?.tasks || []).filter((t) => t.can_push).map((t) => t.task_id)));
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setChecking(false); }
  };

  const pushSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return setError("Select at least one row");
    setPushing(true); setError(""); setPushResult(null);
    try {
      const body = { task_ids: ids };
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      if (podDataUrl) body.pod_data_url = podDataUrl;
      const r = await axios.post(`${base}/api/power-tools/emirates/push`, body);
      setPushResult(r.data);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setPushing(false); }
  };

  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  return (
    <div>
      <Card title="Emirates API Credentials" subtitle="Leave blank to use hardcoded defaults · saved for this session">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <Field label="Client ID"><input value={emiratesCid} onChange={(e) => setEmiratesCid(e.target.value)} placeholder="TMEXAE (optional)" style={INPUT} /></Field>
          <Field label="Client Secret"><PasswordInput value={emiratesCsec} onChange={(e) => setEmiratesCsec(e.target.value)} placeholder="leave blank for default" /></Field>
          <button onClick={testAuth} disabled={authing} style={B.ghost}>{authing ? <Spinner /> : <><i className="fa fa-key" /> Test Auth</>}</button>
        </div>
        {authResult && (
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: authResult.success ? "#f0fdf4" : "#fef2f2", color: authResult.success ? "#166534" : "#991b1b", fontSize: 13 }}>
            {authResult.success ? <><i className="fa fa-check" style={{ marginRight: 6 }} />Authenticated · token expires in {authResult.expires_in}s</> : authResult.error}
          </div>
        )}
        {error && <Alert>{error}</Alert>}
      </Card>

      <Card title="Check AWBs">
        <Field label="AWBs (one per line — digits only)">
          <textarea rows={4} value={awbsText} onChange={(e) => setAwbsText(e.target.value)} placeholder={"310100554473\n310100554528"} style={TEXTAREA} />
        </Field>
        <div style={{ marginTop: 10 }}>
          <button onClick={check} disabled={checking} style={B.primary}>{checking ? <><Spinner /> Checking…</> : <><i className="fa fa-database" /> Check DB</>}</button>
        </div>
      </Card>

      {checking && <Card title="Looking up AWBs…"><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{[100, 75, 85].map((w, i) => <Skel key={i} h={16} w={`${w}%`} />)}</div></Card>}

      {tasks.length > 0 && !checking && (
        <Card title={`${tasks.length} tasks found`}
              subtitle={missing.length ? `${missing.length} not in DB: ${missing.join(", ")}` : "All AWBs resolved"}
              action={
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={pushSelected} disabled={pushing || !selected.size} style={B.success}>
                    {pushing ? <><Spinner /> Pushing…</> : <><i className="fa fa-paper-plane" /> Push {selected.size}</>}
                  </button>
                </div>
              }>
          {/* POD upload */}
          <div style={{ padding: 12, background: "#f8fafc", borderRadius: 8, marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
            <i className="fa fa-image" style={{ color: "#94a3b8" }} />
            <span style={{ fontSize: 12, color: "#475569" }}>Upload POD override (JPEG/PNG, optional):</span>
            <input type="file" accept="image/jpeg,image/png" onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (!f) return;
              if (!/^image\/(jpeg|png)$/i.test(f.type)) return setError("POD must be JPEG or PNG");
              if (f.size > 10 * 1024 * 1024) return setError("POD too large (> 10 MB)");
              const r = new FileReader(); r.onload = (ev) => setPodDataUrl(String(ev.target?.result || "")); r.readAsDataURL(f);
            }} style={{ fontSize: 12 }} />
            {podDataUrl && <img src={podDataUrl} alt="POD" style={{ height: 48, borderRadius: 4, border: "1px solid #e2e8f0" }} />}
          </div>

          <DataTable
            cols={[
              { key: "_chk", label: "✓", render: (t) => <input type="checkbox" checked={selected.has(t.task_id)} disabled={!t.can_push} onChange={() => toggle(t.task_id)} /> },
              { key: "awb",          label: "AWB" },
              { key: "task_status",  label: "Status" },
              { key: "emirates_code", label: "Code", render: (t) => <code>{t.emirates_code || "—"}</code> },
              { key: "emirates_event", label: "Event", render: (t) => <code>{t.emirates_event || "—"}</code> },
              { key: "emirates_exception", label: "Exc", render: (t) => <ExcCode event={t.emirates_event} code={t.emirates_exception} /> },
              { key: "piece_reference", label: "Piece Ref", render: (t) => <code style={{ fontSize: 11 }}>{t.piece_reference || "—"}</code> },
              { key: "has_pod_image", label: "POD", render: (t) => t.has_pod_image
                ? <a href={`${base}${t.pod_image_url}`} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontSize: 12 }}><i className="fa fa-image" /> view</a>
                : <span style={{ color: "#e2e8f0" }}>—</span> },
              { key: "cancel_reason", label: "Reason", render: (t) => <span style={{ fontSize: 11, color: "#64748b" }}>{t.cancel_reason || "—"}</span> },
            ]}
            rows={tasks}
          />
        </Card>
      )}

      {pushResult && (
        <Card title="Push Results" action={<div style={{ display: "flex", gap: 10 }}><Stat label="Success" value={pushResult.success} color="#16a34a" /><Stat label="Failed" value={pushResult.failed} color="#dc2626" /></div>}>
          <DataTable
            cols={[
              { key: "awb",            label: "AWB" },
              { key: "status_code",    label: "Code" },
              { key: "event_code",     label: "Event" },
              { key: "exception_code", label: "Exc" },
              { key: "success",        label: "Result", render: (r) => <Badge variant={r.success ? "success" : "danger"}>{r.success ? "OK" : "FAIL"}</Badge> },
              { key: "pod_included",   label: "POD", render: (r) => r.event_code === "POD"
                ? <Badge variant={r.pod_included ? "success" : "danger"}>{r.pod_included ? "included" : (r.pod_error || "missing")}</Badge>
                : <span style={{ color: "#94a3b8" }}>—</span> },
              { key: "message",        label: "Msg", render: (r) => <span style={{ fontSize: 11 }}>{r.success ? r.message : r.error}</span> },
            ]}
            rows={pushResult.results || []}
          />
        </Card>
      )}

      <EmiratesAutoLoopPanel base={base} emiratesCid={emiratesCid} emiratesCsec={emiratesCsec} />
    </div>
  );
}

// ─── Emirates Auto-Loop ───────────────────────────────────────────
function EmiratesAutoLoopPanel({ base, emiratesCid, emiratesCsec }) {
  const [intervalSec, setIntervalSec] = useState(60);
  const [awbsText, setAwbsText] = useState("");
  const [state, setState] = useState(null);
  const [sentKeys, setSentKeys] = useState(0);
  const [logs, setLogs] = useState([]);
  const [logSrc, setLogSrc] = useState("memory");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [awbFilter, setAwbFilter] = useState("");
  const [error, setError] = useState("");

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${base}/api/power-tools/emirates/auto/status`);
      setState(r.data?.state || null);
      setSentKeys(r.data?.sent_keys_total || 0);
    } catch {}
  }, [base]);

  const refreshLogs = useCallback(async () => {
    try {
      const params = { limit: 200, only_failed: onlyFailed ? "1" : "0" };
      const url = logSrc === "file" ? `${base}/api/power-tools/emirates/auto/file-logs` : `${base}/api/power-tools/emirates/auto/logs`;
      if (logSrc === "file" && awbFilter) params.awb = awbFilter.trim();
      const r = await axios.get(url, { params });
      setLogs(r.data?.logs || []);
    } catch {}
  }, [base, logSrc, onlyFailed, awbFilter]);

  useEffect(() => { poll(); refreshLogs(); const t = setInterval(() => { poll(); refreshLogs(); }, 3000); return () => clearInterval(t); }, [poll, refreshLogs]);

  const start = async () => {
    setError("");
    const awbs = awbsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try {
      const body = { interval: Number(intervalSec) || 60, awbs };
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/emirates/auto/start`, body);
      if (r.data?.error) setError(r.data.error);
      else { setState(r.data?.state); }
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const stop = async () => {
    try { const r = await axios.post(`${base}/api/power-tools/emirates/auto/stop`, {}); setState(r.data?.state); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const running = state?.running;

  return (
    <Card title="Auto Loop"
          subtitle="Background worker — pushes pending Emirates updates on a schedule"
          action={running
            ? <button onClick={stop}  style={B.danger}><i className="fa fa-stop" /> Stop</button>
            : <button onClick={start} style={B.success}><i className="fa fa-play" /> Start</button>}>

      {!running && (
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, marginBottom: 12 }}>
          <Field label="Interval (s)"><input type="number" min={5} value={intervalSec} onChange={(e) => setIntervalSec(e.target.value)} style={INPUT} /></Field>
          <Field label="AWB filter (optional)"><textarea rows={2} value={awbsText} onChange={(e) => setAwbsText(e.target.value)} style={TEXTAREA} /></Field>
        </div>
      )}

      {state && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 12 }}>
          <Stat label="Status"   value={running ? "running" : "idle"} color={running ? "#16a34a" : "#94a3b8"} />
          <Stat label="Cycles"   value={state.cycles} />
          <Stat label="Pushed"   value={state.pushed}  color="#16a34a" />
          <Stat label="Failed"   value={state.failed}  color="#dc2626" />
          <Stat label="Skipped"  value={state.skipped_already_sent} color="#64748b" />
          <Stat label="Sent Keys" value={sentKeys} color="#2563eb" />
        </div>
      )}
      {state?.last_error && <Alert type="error">{state.last_error}</Alert>}
      {error && <Alert>{error}</Alert>}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Logs</span>
        <select value={logSrc} onChange={(e) => setLogSrc(e.target.value)} style={{ ...INPUT, width: "auto", fontSize: 12 }}>
          <option value="memory">Memory (current run)</option>
          <option value="file">File (persistent)</option>
        </select>
        {logSrc === "file" && <input value={awbFilter} onChange={(e) => setAwbFilter(e.target.value)} placeholder="filter AWB" style={{ ...INPUT, width: 140, fontSize: 12 }} />}
        <label style={{ fontSize: 12, color: "#64748b", display: "flex", gap: 4, alignItems: "center" }}>
          <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} /> Failed only
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "#94a3b8" }}>{logs.length} entries</span>
      </div>

      <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <AutoLogTable
          rows={logs}
          fields={[
            { key: "event_datetime", label: "Event time (UTC)", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11, color: "#0f172a" }}>{r.event_datetime ? fmtUtc(r.event_datetime) : "—"}</span> },
            { key: "ts",             label: "Pushed (UTC)",     render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>{r.ts ? fmtUtc(r.ts) : "—"}</span> },
            { key: "awb",            label: "AWB" },
            { key: "task_status",    label: "Status" },
            { key: "event_code",     label: "Event",  render: (r) => <code>{r.event_code || "—"}</code> },
            { key: "exception_code", label: "Exc",    render: (r) => <ExcCode event={r.event_code} code={r.exception_code} /> },
            { key: "ok",             label: "Result", render: (r) => <Badge variant={r.ok ? "success" : "danger"}>{r.ok ? "OK" : "FAIL"}</Badge> },
            { key: "msg",            label: "Message", render: (r) => <span style={{ fontSize: 11 }}>{r.ok ? r.message : r.error}</span> },
          ]}
        />
      </div>
    </Card>
  );
}

// ─── Scan Builder Panel ───────────────────────────────────────────
function ScanBuilderPanel({ base, creds }) {
  const { emiratesCid, emiratesCsec, setEmiratesCid, setEmiratesCsec } = creds;
  const [awb, setAwb] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [previewMap, setPreviewMap] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [rowState, setRowState] = useState({});
  const [rowResult, setRowResult] = useState({});
  const [running, setRunning] = useState(false);
  const cancelRef = useRef(false);
  const [counts, setCounts] = useState({ total: 0, sent: 0, failed: 0, podPending: 0 });
  const [failedLogs, setFailedLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [logAwbFilter, setLogAwbFilter] = useState("");
  const [error, setError] = useState("");

  const loadTimeline = async () => {
    const a = awb.trim();
    if (!a) return setError("AWB is required");
    setLoading(true); setError(""); setTimeline(null); setSelected(new Set());
    setRowState({}); setRowResult({}); setCounts({ total: 0, sent: 0, failed: 0, podPending: 0 });
    try {
      const r = await axios.post(`${base}/api/power-tools/scan-builder/load`, { awb: a });
      setTimeline(r.data || null);
      setSelected(new Set((r.data?.scans || []).filter((s) => s.can_push).map((s) => s.seq)));
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const loadPreviews = async () => {
    if (!timeline) return;
    const scans = (timeline.scans || []).filter((s) => selected.has(s.seq)).map((s) => ({ seq: s.seq }));
    if (!scans.length) return setError("Select at least one scan");
    setPreviewing(true); setError("");
    try {
      const r = await axios.post(`${base}/api/power-tools/scan-builder/preview`, { awb: timeline.task.awb, scans });
      const m = {};
      (r.data?.previews || []).forEach((p) => { m[p.seq] = p; });
      setPreviewMap(m);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setPreviewing(false); }
  };

  const pushSeq = async () => {
    const seqs = (timeline.scans || []).filter((s) => selected.has(s.seq)).map((s) => s.seq);
    if (!seqs.length) return setError("Select at least one scan");
    cancelRef.current = false; setRunning(true); setError("");
    let sent = 0, failed = 0, pod = 0;
    setCounts({ total: seqs.length, sent: 0, failed: 0, podPending: 0 });
    setRowState((c) => { const n = { ...c }; seqs.forEach((q) => { n[q] = "queued"; }); return n; });

    for (const seq of seqs) {
      if (cancelRef.current) break;
      setRowState((c) => ({ ...c, [seq]: "pushing" }));
      try {
        const body = { awb: timeline.task.awb, scan: { seq } };
        if (emiratesCid) body.client_id = emiratesCid;
        if (emiratesCsec) body.client_secret = emiratesCsec;
        const r = await axios.post(`${base}/api/power-tools/scan-builder/push`, body);
        const d = r.data || {};
        setRowResult((c) => ({ ...c, [seq]: d }));
        if (d.pod_pending) { setRowState((c) => ({ ...c, [seq]: "pod-pending" })); pod++; }
        else if (d.success) { setRowState((c) => ({ ...c, [seq]: "sent" })); sent++; }
        else { setRowState((c) => ({ ...c, [seq]: "fail" })); failed++; }
      } catch (e) {
        const d = { success: false, error: e?.response?.data?.error || e.message };
        setRowResult((c) => ({ ...c, [seq]: d }));
        setRowState((c) => ({ ...c, [seq]: "fail" }));
        failed++;
      }
      setCounts({ total: seqs.length, sent, failed, podPending: pod });
      await new Promise((r) => setTimeout(r, 400));
    }
    setRunning(false);
    refreshFailedLogs();
  };

  const pushOne = async (scan) => {
    setRowState((c) => ({ ...c, [scan.seq]: "pushing" }));
    try {
      const body = { awb: timeline.task.awb, scan: { seq: scan.seq } };
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/scan-builder/push`, body);
      const d = r.data || {};
      setRowResult((c) => ({ ...c, [scan.seq]: d }));
      setRowState((c) => ({ ...c, [scan.seq]: d.pod_pending ? "pod-pending" : d.success ? "sent" : "fail" }));
    } catch (e) {
      setRowResult((c) => ({ ...c, [scan.seq]: { success: false, error: e?.response?.data?.error || e.message } }));
      setRowState((c) => ({ ...c, [scan.seq]: "fail" }));
    }
    refreshFailedLogs();
  };

  const toggle = (seq) => { const n = new Set(selected); n.has(seq) ? n.delete(seq) : n.add(seq); setSelected(n); };

  const refreshFailedLogs = async () => {
    setLogsLoading(true);
    try {
      const params = { limit: 100, only_failed: "1" };
      if (logAwbFilter) params.awb = logAwbFilter.trim();
      const r = await axios.get(`${base}/api/power-tools/scan-builder/logs`, { params });
      setFailedLogs(r.data?.logs || []);
    } catch {}
    finally { setLogsLoading(false); }
  };
  useEffect(() => { refreshFailedLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const retryLog = async (id) => {
    setRetryingId(id);
    try {
      const body = {};
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      await axios.post(`${base}/api/power-tools/scan-builder/retry/${id}`, body);
      await refreshFailedLogs();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setRetryingId(null); }
  };

  const stBadge = (st) => {
    if (!st) return null;
    const m = { queued: ["default", "queued"], pushing: ["warn", "sending…"], sent: ["success", "SENT"], fail: ["danger", "FAIL"], "pod-pending": ["warn", "POD pending"] };
    const [v, l] = m[st] || ["default", st];
    return <Badge variant={v}>{l}</Badge>;
  };

  return (
    <div>
      <Card title="Emirates API Credentials" subtitle="Saved for this session">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Client ID (optional override)"><input value={emiratesCid} onChange={(e) => setEmiratesCid(e.target.value)} style={INPUT} /></Field>
          <Field label="Client Secret"><PasswordInput value={emiratesCsec} onChange={(e) => setEmiratesCsec(e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Load Scan Timeline">
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <Field label="AWB" style={{ flex: 1 }}><input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="310100554473" style={INPUT} onKeyDown={(e) => e.key === "Enter" && loadTimeline()} /></Field>
          <button onClick={loadTimeline} disabled={loading} style={B.primary}>{loading ? <><Spinner /> Loading…</> : <><i className="fa fa-list-ul" /> Load Timeline</>}</button>
        </div>
        {error && <Alert>{error}</Alert>}
      </Card>

      {loading && (
        <Card title="Building timeline…">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[100, 80, 90, 70, 95].map((w, i) => <Skel key={i} h={14} w={`${w}%`} />)}
          </div>
        </Card>
      )}

      {timeline && !loading && (
        <Card title={`Timeline — ${timeline.task.awb}`}
              subtitle={`Task #${timeline.task.task_id} · ${timeline.task.piece_reference ? `Piece: ${timeline.task.piece_reference}` : "⚠ No piece reference"} · Current: ${timeline.task.current_status}`}
              action={
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={loadPreviews} disabled={previewing || !selected.size || running} style={B.ghost}>
                    {previewing ? <Spinner /> : <><i className="fa fa-eye" /> Preview {selected.size}</>}
                  </button>
                  {running
                    ? <button onClick={() => { cancelRef.current = true; }} style={B.danger}><i className="fa fa-stop" /> Stop</button>
                    : <button onClick={pushSeq} disabled={!selected.size} style={B.success}><i className="fa fa-paper-plane" /> Push {selected.size} sequentially</button>}
                </div>
              }>

          {/* Live counters */}
          {counts.total > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
              <Stat label="Sent"       value={counts.sent}       color="#16a34a" />
              <Stat label="Failed"     value={counts.failed}     color="#dc2626" />
              <Stat label="POD pend."  value={counts.podPending} color="#d97706" />
              <Stat label="Remaining"  value={counts.total - counts.sent - counts.failed - counts.podPending} color="#2563eb" />
            </div>
          )}

          <DataTable
            cols={[
              { key: "_chk", label: "✓", render: (s) => <input type="checkbox" disabled={!s.can_push || running} checked={selected.has(s.seq)} onChange={() => toggle(s.seq)} /> },
              { key: "seq",            label: "#" },
              { key: "event_datetime", label: "Event time (UTC)", render: (s) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{s.event_datetime ? fmtUtc(s.event_datetime) : "—"}</span> },
              { key: "detected_at",    label: "Detected (UTC)",   render: (s) => {
                const r = rowResult[s.seq]; return <span style={{ fontFamily: "monospace", fontSize: 11, color: "#94a3b8" }}>{r?.detected_at ? fmtUtc(r.detected_at) : "—"}</span>;
              }},
              { key: "task_status",    label: "Status",   render: (s) => <>{s.task_status}{s.source === "current" && <> <Badge variant="info">current</Badge></>}</> },
              { key: "reason",         label: "Reason",   render: (s) => <span style={{ fontSize: 11, color: "#64748b" }}>{s.reason || "—"}</span> },
              { key: "emirates_event", label: "Event",    render: (s) => <code>{s.emirates_event || "—"}</code> },
              { key: "exception_code", label: "Exc 🔒",   render: (s) => <ExcCode event={s.emirates_event} code={s.emirates_exception} /> },
              { key: "_pod",           label: "POD",      render: (s) => {
                if (s.pod_url) return <a href={`${base}${s.pod_image_url}`} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}><i className="fa fa-image" /></a>;
                if (s.emirates_event === "POD") return <span style={{ color: "#d97706", fontSize: 11 }} title="POD required but not yet available"><i className="fa fa-clock-o" /> pending</span>;
                return <span style={{ color: "#e2e8f0" }}>—</span>;
              }},
              { key: "_state",         label: "State",    render: (s) => stBadge(rowState[s.seq]) || "—" },
              { key: "_action",        label: "",         render: (s) => {
                const st = rowState[s.seq];
                const r = rowResult[s.seq];
                return (
                  <div>
                    <button onClick={() => pushOne(s)} disabled={!s.can_push || running || st === "pushing"} style={{ ...B.ghost, padding: "4px 10px", fontSize: 11 }}>
                      {st === "pushing" ? <Spinner /> : (st === "fail" || st === "pod-pending") ? <><i className="fa fa-redo" /> retry</> : <><i className="fa fa-paper-plane" /> push</>}
                    </button>
                    {r && (r.error || r.message) && (
                      <div style={{ fontSize: 10, color: r.success ? "#166534" : "#991b1b", marginTop: 3, maxWidth: 200, wordBreak: "break-word" }}>
                        {r.success ? r.message : r.error}
                      </div>
                    )}
                  </div>
                );
              }},
            ]}
            rows={timeline.scans || []}
          />

          {/* Preview payloads */}
          {previewMap && (
            <div style={{ marginTop: 14, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: "#475569", marginBottom: 8 }}>PAYLOAD PREVIEW</div>
              {Object.values(previewMap).map((p) => (
                <details key={p.seq} style={{ marginBottom: 6, border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  <summary style={{ padding: "8px 12px", cursor: "pointer", background: "#f8fafc", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>seq {p.seq}</span>
                    <span style={{ color: "#64748b", fontSize: 12 }}>{p.scan?.task_status} · {p.scan?.emirates_event || "—"}</span>
                    {p.error && <Badge variant="danger">{p.error}</Badge>}
                  </summary>
                  {p.payload && <pre style={{ margin: 0, padding: 12, fontSize: 11, background: "#0f172a", color: "#a5f3fc", overflowX: "auto" }}>{JSON.stringify(p.payload, null, 2)}</pre>}
                </details>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card title={`Failed Push Audit — ${failedLogs.length} entries`}
            subtitle="Persisted in primary DB · click Retry to re-push with a fresh token"
            action={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={logAwbFilter} onChange={(e) => setLogAwbFilter(e.target.value)} placeholder="filter AWB" style={{ ...INPUT, width: 160, fontSize: 12 }} />
                <button onClick={refreshFailedLogs} disabled={logsLoading} style={B.ghost}>{logsLoading ? <Spinner /> : <><i className="fa fa-refresh" /></>}</button>
              </div>
            }>
        <DataTable
          cols={[
            { key: "id",          label: "ID" },
            { key: "awb",         label: "AWB" },
            { key: "seq",         label: "#" },
            { key: "task_status", label: "Status" },
            { key: "emirates_event",    label: "Event", render: (r) => <code>{r.emirates_event || "—"}</code> },
            { key: "exception_code",    label: "Exc 🔒", render: (r) => <ExcCode event={r.emirates_event} code={r.exception_code} /> },
            { key: "event_at",    label: "Event time (UTC)",    render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtUtc(r.event_at)}</span> },
            { key: "detected_at", label: "Detected (UTC)",      render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtUtc(r.detected_at)}</span> },
            { key: "pushed_at",   label: "Last push (UTC)",     render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtUtc(r.pushed_at)}</span> },
            { key: "attempts",    label: "Tries" },
            { key: "last_error",  label: "Error", render: (r) => <span style={{ fontSize: 11, color: "#dc2626", fontFamily: "monospace" }}>{r.last_error || r.pod_error || "—"}</span> },
            { key: "_retry",      label: "", render: (r) => (
              <button onClick={() => retryLog(r.id)} disabled={retryingId === r.id} style={{ ...B.primary, padding: "4px 10px", fontSize: 11 }}>
                {retryingId === r.id ? <Spinner /> : <><i className="fa fa-redo" /> retry</>}
              </button>
            )},
          ]}
          rows={failedLogs}
          emptyMsg="No failed pushes — looking good"
        />
      </Card>

      <ScanBuilderAutoLoopPanel base={base} emiratesCid={emiratesCid} emiratesCsec={emiratesCsec} />
    </div>
  );
}

// ─── Scan Builder Auto Loop ───────────────────────────────────────
function ScanBuilderAutoLoopPanel({ base, emiratesCid, emiratesCsec }) {
  const [intervalSec, setIntervalSec] = useState(60);
  const [awbsText, setAwbsText] = useState("");
  const [state, setState] = useState(null);
  const [sentKeys, setSentKeys] = useState(0);
  const [failedInMem, setFailedInMem] = useState(0);
  const [logs, setLogs] = useState([]);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [error, setError] = useState("");

  // ─── retry state ──────────────────────────────────────────────
  const [retryingKey, setRetryingKey] = useState(null);  // awb|seq currently retrying
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [retryBanner, setRetryBanner] = useState(null);

  const retryOne = async (awb, seq) => {
    setRetryBanner(null);
    setRetryingKey(`${awb}|${seq}`);
    try {
      const body = { awb, seq };
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/scan-builder/auto/retry-one`, body);
      const d = r.data || {};
      if (d.error) setRetryBanner({ kind: "err", text: `Retry failed: ${d.error}` });
      else if (Boolean(d.ok)) setRetryBanner({ kind: "ok", text: `Retried ${awb} (seq ${seq}) — SUCCESS` });
      else setRetryBanner({ kind: "err", text: `Retried ${awb} (seq ${seq}) — FAIL: ${d.error || d.message || "see log"}` });
      await getLogs();
    } catch (e) {
      setRetryBanner({ kind: "err", text: e?.response?.data?.error || e.message });
    } finally {
      setRetryingKey(null);
    }
  };

  const retryAllFailed = async () => {
    setRetryBanner(null);
    setBulkRetrying(true);
    try {
      const body = {};
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/scan-builder/auto/retry-failed`, body);
      const d = r.data || {};
      setRetryBanner({
        kind: d.failed === 0 ? "ok" : "warn",
        text: `Retried ${d.retried || 0} failed entries · ${d.succeeded || 0} succeeded · ${d.failed || 0} still failed`,
      });
      await getLogs();
    } catch (e) {
      setRetryBanner({ kind: "err", text: e?.response?.data?.error || e.message });
    } finally {
      setBulkRetrying(false);
    }
  };

  // ─── sent-log import ──────────────────────────────────────────
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImportClick = () => { setImportResult(null); fileInputRef.current?.click(); };
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jsonl|json|txt)$/i.test(file.name)) {
      setImportResult({ error: "File must be .jsonl (one JSON object per line)" });
      e.target.value = ""; return;
    }
    setImporting(true);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await axios.post(`${base}/api/power-tools/scan-builder/auto/import-sent`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      // Refresh status so sent-keys counter updates immediately
      try { const s = await axios.get(`${base}/api/power-tools/scan-builder/auto/status`); setSentKeys(s.data?.sent_keys_total || 0); } catch {}
    } catch (err) {
      setImportResult({ error: err?.response?.data?.error || err.message });
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${base}/api/power-tools/scan-builder/auto/status`);
      setState(r.data?.state || null);
      setSentKeys(r.data?.sent_keys_total || 0);
      setFailedInMem(r.data?.failed_in_memory || 0);
    } catch {}
  }, [base]);
  const getLogs = useCallback(async () => {
    try {
      const r = await axios.get(`${base}/api/power-tools/scan-builder/auto/logs`, { params: { limit: 200, only_failed: onlyFailed ? "1" : "0" } });
      setLogs(r.data?.logs || []);
    } catch {}
  }, [base, onlyFailed]);

  useEffect(() => { poll(); getLogs(); const t = setInterval(() => { poll(); getLogs(); }, 3000); return () => clearInterval(t); }, [poll, getLogs]);

  const start = async () => {
    setError("");
    const awbs = awbsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try {
      const body = { interval: Number(intervalSec) || 60, awbs };
      if (emiratesCid) body.client_id = emiratesCid;
      if (emiratesCsec) body.client_secret = emiratesCsec;
      const r = await axios.post(`${base}/api/power-tools/scan-builder/auto/start`, body);
      if (r.data?.error) setError(r.data.error);
      else setState(r.data?.state);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const stop = async () => {
    try { const r = await axios.post(`${base}/api/power-tools/scan-builder/auto/stop`, {}); setState(r.data?.state); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const running = state?.running;

  return (
    <Card title="Scan Builder Auto Loop"
          subtitle="Walks the full history timeline for every eligible AWB and pushes each scan in order"
          action={
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input ref={fileInputRef} type="file" accept=".jsonl,.json,.txt" onChange={onImportFile} style={{ display: "none" }} />
              <button onClick={handleImportClick} disabled={importing || running} style={B.ghost}
                      title="Import a sent-log JSONL — those keys will be marked already-sent and skipped by the loop">
                {importing ? <><Spinner /> Importing…</> : <><i className="fa fa-upload" /> Import sent-log</>}
              </button>
              {running
                ? <button onClick={stop}  style={B.danger}><i className="fa fa-stop" /> Stop</button>
                : <button onClick={start} style={B.success}><i className="fa fa-play" /> Start</button>}
            </div>
          }>

      {importResult && (
        importResult.error
          ? <Alert>{importResult.error}</Alert>
          : <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "#f0fdf4", color: "#166534", fontSize: 13, display: "flex", alignItems: "center", gap: 16 }}>
              <i className="fa fa-check-circle" />
              <span><strong>{importResult.imported}</strong> keys imported · <strong>{importResult.skipped}</strong> already known/skipped · <strong>{importResult.errors}</strong> parse errors</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#475569" }}>Total sent-keys: {importResult.sent_keys_total}</span>
            </div>
      )}

      {!running && (
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, marginBottom: 12 }}>
          <Field label="Interval (s)"><input type="number" min={5} value={intervalSec} onChange={(e) => setIntervalSec(e.target.value)} style={INPUT} /></Field>
          <Field label="AWB filter"><textarea rows={2} value={awbsText} onChange={(e) => setAwbsText(e.target.value)} style={TEXTAREA} /></Field>
        </div>
      )}

      {state && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 12 }}>
          <Stat label="Status"   value={running ? "running" : "idle"} color={running ? "#16a34a" : "#94a3b8"} />
          <Stat label="Cycles"   value={state.cycles} />
          <Stat label="Pushed"   value={state.pushed}  color="#16a34a" />
          <Stat label="Failed"   value={state.failed}  color="#dc2626" />
          <Stat label="Skipped"  value={state.skipped_already_sent} color="#64748b" />
          <Stat label="Sent Keys" value={sentKeys} color="#2563eb" />
        </div>
      )}
      {state?.last_error && <Alert>{state.last_error}</Alert>}
      {error && <Alert>{error}</Alert>}

      {retryBanner && (
        <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                      background: retryBanner.kind === "ok" ? "#f0fdf4" : retryBanner.kind === "warn" ? "#fffbeb" : "#fef2f2",
                      color:      retryBanner.kind === "ok" ? "#166534" : retryBanner.kind === "warn" ? "#92400e" : "#991b1b" }}>
          <i className={`fa ${retryBanner.kind === "ok" ? "fa-check-circle" : retryBanner.kind === "warn" ? "fa-exclamation-circle" : "fa-times-circle"}`} style={{ marginRight: 6 }} />
          {retryBanner.text}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, marginTop: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Recent logs</span>
        <label style={{ fontSize: 12, color: "#64748b", display: "flex", gap: 4, alignItems: "center" }}>
          <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} /> Failed only
        </label>
        {/* Bulk retry — only enabled when there are failed entries in memory. */}
        <button onClick={retryAllFailed}
                disabled={bulkRetrying || failedInMem === 0}
                style={{ ...B.danger, padding: "5px 12px", fontSize: 12, opacity: failedInMem === 0 ? 0.45 : 1 }}
                title={failedInMem === 0 ? "No failed entries to retry" : `Retry ${failedInMem} failed entries`}>
          {bulkRetrying
            ? <><Spinner /> Retrying {failedInMem}…</>
            : <><i className="fa fa-redo" /> Retry failed ({failedInMem})</>}
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "#94a3b8" }}>{logs.length} entries</span>
      </div>

      <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <AutoLogTable
          rows={logs}
          fields={[
            { key: "event_datetime", label: "Event time (UTC)", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11, color: "#0f172a" }}>{r.event_datetime ? fmtUtc(r.event_datetime) : "—"}</span> },
            { key: "ts",             label: "Pushed (UTC)",     render: (r) => <span style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>{r.ts ? fmtUtc(r.ts) : "—"}</span> },
            { key: "awb",            label: "AWB" },
            { key: "seq",            label: "seq" },
            { key: "task_status",    label: "Status" },
            { key: "event_code",     label: "Event",  render: (r) => <code>{r.event_code || "—"}</code> },
            { key: "exception_code", label: "Exc",    render: (r) => <ExcCode event={r.event_code} code={r.exception_code} /> },
            { key: "ok",             label: "Result", render: (r) => (
              <>
                <Badge variant={r.ok ? "success" : "danger"}>{r.ok ? "OK" : "FAIL"}</Badge>
                {r.retry && <span style={{ marginLeft: 4, fontSize: 9, color: "#64748b", fontStyle: "italic" }}>retry</span>}
              </>
            )},
            { key: "msg",            label: "Message", render: (r) => <span style={{ fontSize: 11 }}>{r.ok ? r.message : r.error}</span> },
            // Per-row retry. Stop event propagation so clicking the button
            // doesn't also expand the JSON viewer row.
            { key: "_retry",         label: "", render: (r) => {
              const seq = typeof r.seq === "number" ? r.seq : parseInt(r.seq, 10);
              if (r.ok || !r.awb || isNaN(seq)) return null;
              const key = `${r.awb}|${seq}`;
              const busy = retryingKey === key;
              return (
                <button onClick={(ev) => { ev.stopPropagation(); retryOne(r.awb, seq); }}
                        disabled={busy || bulkRetrying}
                        style={{ ...B.ghost, padding: "3px 8px", fontSize: 11 }}
                        title={`Re-push ${r.awb} (seq ${seq}) using a fresh timeline lookup`}>
                  {busy ? <Spinner /> : <><i className="fa fa-redo" /> retry</>}
                </button>
              );
            }},
          ]}
        />
      </div>
    </Card>
  );
}
